import {
  Meta,
  Links,
  ScrollRestoration,
  Scripts,
  useRouteLoaderData,
} from "@remix-run/react";

import { HostContext } from "./constants";
import { env } from "./utils/helpers";
import { useGlobalComponents } from "./hooks/useGlobalComponents";
import { HeadScripts } from "./components/scripts/HeadScripts";
import { TrackingScript, TrackingIframe } from "./components/tracking/Tracking";
import { Page } from "./components/page/Page";
import { When } from "./components/when/When";
import { I18nProvider } from "./components/i18n/I18nProvider";

import type { loader } from "./root";

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root");

  const { includeGlobalUI, GlobalMeta, GlobalHeader, GlobalFooter } =
    useGlobalComponents({
      hostContext: data?.hostContext ?? HostContext.Unknown,
    });

  if (data?.isStencil) {
    return (
      <html lang={data.locale}>
        <head>
          <GlobalMeta />
          <Meta />
          <HeadScripts />
        </head>
        <body data-context={HostContext.MobileNative}>{children}</body>
      </html>
    );
  }

  return (
    <I18nProvider locale={data?.locale} dict={data?.labels}>
      <html lang={data?.locale}>
        <head>
          <GlobalMeta />
          <Meta />
          <HeadScripts />
          <Links />
          <TrackingScript
            gtmId={env("GTM_CONTAINER_ID")}
            userId={data?.currentUser?.publicUserId}
          />
        </head>
        <body data-context={data?.hostContext}>
          <TrackingIframe gtmId={env("GTM_CONTAINER_ID")} />

          <Page>
            <When condition={includeGlobalUI}>
              <Page.Header>
                <GlobalHeader />
              </Page.Header>
            </When>

            <Page.Body>{children}</Page.Body>

            <When condition={includeGlobalUI}>
              <Page.Footer>
                <GlobalFooter />
              </Page.Footer>
            </When>
          </Page>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    </I18nProvider>
  );
}
